const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
const NAIVE_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const formatterCache = new Map();

export function browserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch (error) {
        return "UTC";
    }
}

function dateFormatter(timeZone, includeDate) {
    const zone = timeZone || browserTimeZone();
    const key = `${zone}|${includeDate ? "full" : "axis"}`;
    const cached = formatterCache.get(key);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: zone,
        ...(includeDate
            ? { year: "numeric", month: "2-digit", day: "2-digit" }
            : {}),
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        timeZoneName: "short",
    });
    formatterCache.set(key, formatter);
    return formatter;
}

export function parseRecordedTimestamp(value) {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    if (value == null) {
        return null;
    }
    const text = String(value).trim();
    if (!text || text === "N/A") {
        return null;
    }
    if (HAS_ZONE.test(text)) {
        return parseZonedTimestamp(text);
    }
    return parseNaiveAsUtc(text);
}

function parseZonedTimestamp(text) {
    const direct = new Date(text);
    if (!Number.isNaN(direct.getTime())) {
        return direct;
    }
    const trimmed = text.replace(/(\.\d{3})\d+/, "$1");
    if (trimmed !== text) {
        const retry = new Date(trimmed);
        if (!Number.isNaN(retry.getTime())) {
            return retry;
        }
    }
    return parseZonedComponents(trimmed);
}

function parseZonedComponents(text) {
    const match = text.match(
        /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?(\d{2}))$/i
    );
    if (!match) {
        return null;
    }
    const milliseconds = match[7] ? Number(match[7].slice(0, 3).padEnd(3, "0")) : 0;
    let offsetMinutes = 0;
    if (match[8].toUpperCase() !== "Z") {
        const sign = match[8].startsWith("-") ? -1 : 1;
        const hours = Number(match[8].replace(/^[+-]/, "").slice(0, 2));
        const minutes = Number(match[9]);
        offsetMinutes = sign * (hours * 60 + minutes);
    }
    const utc = Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]) - offsetMinutes,
        Number(match[6] || 0),
        milliseconds
    );
    const parsed = new Date(utc);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseNaiveAsUtc(text) {
    const match = text.match(NAIVE_TIMESTAMP);
    if (!match) {
        return null;
    }
    const clock = match[2].length === 5 ? `${match[2]}:00` : match[2];
    const parsed = new Date(`${match[1]}T${clock}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatLocalDateTime(value, timeZone = browserTimeZone()) {
    if (value == null || value === "" || value === "N/A") {
        return "N/A";
    }
    const parsed = parseRecordedTimestamp(value);
    if (!parsed) {
        return "Invalid time";
    }
    return dateFormatter(timeZone, true).format(parsed);
}

export function formatLocalAxisTime(value, timeZone = browserTimeZone()) {
    const parsed = parseRecordedTimestamp(value);
    if (!parsed) {
        return "";
    }
    return dateFormatter(timeZone, false).format(parsed);
}

function zoneParts(utcMs, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const parts = {};
    formatter.formatToParts(new Date(utcMs)).forEach((part) => {
        if (part.type !== "literal") {
            parts[part.type] = part.value;
        }
    });
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return {
        key: `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`,
        wallAsUtc: Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            Number(hour),
            Number(parts.minute),
            Number(parts.second)
        ),
    };
}

function zoneOffsetMs(utcMs, timeZone) {
    return zoneParts(utcMs, timeZone).wallAsUtc - utcMs;
}

function wallKey(utcMs, timeZone) {
    return zoneParts(utcMs, timeZone).key;
}

function localInputParts(value) {
    const match = String(value ?? "").trim().match(LOCAL_INPUT);
    if (!match) {
        return null;
    }
    const second = match[6] || "00";
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4]),
        minute: Number(match[5]),
        second: Number(second),
        key: `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${second}`,
    };
}

export function instantsForLocalInput(value, timeZone = browserTimeZone()) {
    const parts = localInputParts(value);
    if (!parts) {
        return [];
    }
    const wallAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
    );
    const seed = wallAsUtc - zoneOffsetMs(wallAsUtc, timeZone);
    const guesses = new Set([seed, wallAsUtc - zoneOffsetMs(seed, timeZone)]);
    [-3, -2, -1, 1, 2, 3].forEach((hours) => {
        const probe = seed + hours * 60 * 60 * 1000;
        guesses.add(wallAsUtc - zoneOffsetMs(probe, timeZone));
    });
    return [...guesses]
        .filter((ms) => wallKey(ms, timeZone) === parts.key)
        .sort((left, right) => left - right)
        .filter((ms, index, all) => index === 0 || ms !== all[index - 1]);
}

function firstValidInstant(value, timeZone) {
    const parts = localInputParts(value);
    if (!parts) {
        return null;
    }
    const wallAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
    );
    let cursor = wallAsUtc - zoneOffsetMs(wallAsUtc, timeZone);
    for (let step = 0; step < 240; step += 1) {
        if (wallKey(cursor, timeZone) >= parts.key) {
            return cursor;
        }
        cursor += 60 * 1000;
    }
    return null;
}

export function historyQueryTime(value, edge = "start", timeZone = browserTimeZone()) {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    const text = String(value ?? "").trim();
    if (!text) {
        return null;
    }
    if (HAS_ZONE.test(text)) {
        const parsed = parseRecordedTimestamp(text);
        return parsed ? parsed.toISOString() : null;
    }
    const instants = instantsForLocalInput(text, timeZone);
    if (instants.length > 0) {
        const chosen = edge === "end" ? instants[instants.length - 1] : instants[0];
        return new Date(chosen).toISOString();
    }
    const shifted = firstValidInstant(text, timeZone);
    return shifted == null ? null : new Date(shifted).toISOString();
}

export function localSecondGroupKey(value, timeZone = browserTimeZone()) {
    const parsed = parseRecordedTimestamp(value);
    if (!parsed) {
        return `unparsed:${String(value)}`;
    }
    const secondMs = Math.floor(parsed.getTime() / 1000) * 1000;
    return `${wallKey(secondMs, timeZone)}|${zoneOffsetMs(secondMs, timeZone)}`;
}
